import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/layout/PageLayout';
import { gameService, InsightRound } from '../services/gameService';
import artistCollection from '../assets/artistCollection.json';
import artistAliases from '../assets/artistAliases.json';
import { getRatingTags, Rating, RATING_OPTIONS } from '../constants/rating';
import styles from './InsightPage.module.css';

type GamePhase = 'setup' | 'loading' | 'playing' | 'result';

const InsightPage: React.FC = () => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<GamePhase>('setup');
  const [error, setError] = useState<string | null>(null);
  
  // Setup state
  const [artistRangeStart, setArtistRangeStart] = useState(1);
  const [artistRangeEnd, setArtistRangeEnd] = useState(100);
  const [rating, setRating] = useState<Rating>('all');
  
  // Game state
  const [round, setRound] = useState<InsightRound | null>(null);
  const [guess, setGuess] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  
  // Sound effects
  const correctSoundRef = useRef<HTMLAudioElement | null>(null);
  const incorrectSoundRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    correctSoundRef.current = new Audio('/sounds/correct.mp3');
    incorrectSoundRef.current = new Audio('/sounds/incorrect.mp3');
    if (correctSoundRef.current) correctSoundRef.current.volume = 0.5;
    if (incorrectSoundRef.current) incorrectSoundRef.current.volume = 0.5;
  }, []);

  const selectedArtists = useMemo(() => {
    return artistCollection
      .slice(artistRangeStart - 1, artistRangeEnd)
      .map(a => a.name);
  }, [artistRangeStart, artistRangeEnd]);

  const autocompleteOptions = useMemo(() => {
    const aliases = artistAliases.map(item => item.alias);
    return Array.from(new Set([...selectedArtists, ...aliases]));
  }, [selectedArtists]);

  const handleStartGame = async () => {
    setPhase('loading');
    setError(null);

    try {
      const ratingTags = getRatingTags(rating);
      const insightRound = await gameService.getInsightRound({
        selectedArtists,
        ratingTags: ratingTags || undefined,
      });
      setRound(insightRound);
      setPhase('playing');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setPhase('setup');
    }
  };

  const handleGuessChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const oldValue = guess;
    const newValue = e.target.value;

    if (autocompleteOptions.includes(oldValue) && newValue.length > oldValue.length) {
      return;
    }

    setGuess(newValue);

    if (newValue.length >= 2) {
      const filteredSuggestions = autocompleteOptions.filter(artist =>
        artist.toLowerCase().includes(newValue.toLowerCase())
      );

      if (filteredSuggestions.length === 1 && newValue.length > oldValue.length) {
        setGuess(filteredSuggestions[0]);
        setSuggestions([]);
      } else {
        setSuggestions(filteredSuggestions);
      }
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setGuess(suggestion);
    setSuggestions([]);
  };

  const handleSubmitGuess = (e: React.FormEvent) => {
    e.preventDefault();
    if (!round) return;

    const aliasMap = new Map(artistAliases.map(item => [item.alias.toLowerCase(), item.trueName]));
    const submittedGuess = guess.trim().toLowerCase();
    const userGuess = aliasMap.get(submittedGuess) || submittedGuess;
    const correctAnswer = round.correctAnswer.toLowerCase();
    const correct = userGuess === correctAnswer;

    setIsCorrect(correct);
    setPhase('result');

    if (correct) {
      correctSoundRef.current?.play().catch(() => {});
    } else {
      incorrectSoundRef.current?.play().catch(() => {});
    }
  };

  const handlePlayAgain = () => {
    setPhase('setup');
    setRound(null);
    setGuess('');
    setIsCorrect(null);
    setSuggestions([]);
  };

  // Render analysis with proper formatting
  const renderAnalysis = (analysis: string) => {
    const sections = analysis.split(/(?=\d+\.\s)/);
    return sections.map((section, index) => {
      const lines = section.trim().split('\n');
      const title = lines[0];
      const content = lines.slice(1).join('\n');
      
      if (!title) return null;
      
      return (
        <div key={index} className={styles.analysisSection}>
          <h3 className={styles.sectionTitle}>{title}</h3>
          <p className={styles.sectionContent}>{content}</p>
        </div>
      );
    });
  };

  return (
    <PageLayout>
      <div className={styles.container}>
        <h1 className={styles.title}>Insight Mode</h1>
        <p className={styles.subtitle}>
          Guess the artist based on AI analysis — no image shown!
        </p>

        {phase === 'setup' && (
          <div className={styles.setupContainer}>
            <div className={styles.settingGroup}>
              <label className={styles.label}>Artist Range</label>
              <div className={styles.rangeInputs}>
                <input
                  type="number"
                  min={1}
                  max={artistCollection.length}
                  value={artistRangeStart}
                  onChange={(e) => setArtistRangeStart(Math.max(1, parseInt(e.target.value) || 1))}
                  className={styles.rangeInput}
                />
                <span>to</span>
                <input
                  type="number"
                  min={1}
                  max={artistCollection.length}
                  value={artistRangeEnd}
                  onChange={(e) => setArtistRangeEnd(Math.min(artistCollection.length, parseInt(e.target.value) || artistCollection.length))}
                  className={styles.rangeInput}
                />
                <span className={styles.artistCount}>({selectedArtists.length} artists)</span>
              </div>
            </div>

            <div className={styles.settingGroup}>
              <label className={styles.label}>Rating Filter</label>
              <select
                value={rating}
                onChange={(e) => setRating(e.target.value as Rating)}
                className={styles.select}
              >
                {RATING_OPTIONS.map(opt => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </select>
            </div>

            {error && <p className={styles.error}>{error}</p>}

            <button
              onClick={handleStartGame}
              className={styles.startButton}
              disabled={selectedArtists.length === 0}
            >
              Start Insight Round
            </button>
          </div>
        )}

        {phase === 'loading' && (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>Analyzing artwork with AI...</p>
            <p className={styles.loadingSubtext}>This may take a moment</p>
          </div>
        )}

        {phase === 'playing' && round && (
          <div className={styles.playingContainer}>
            <div className={styles.analysisContainer}>
              {renderAnalysis(round.analysis)}
            </div>

            <form onSubmit={handleSubmitGuess} className={styles.guessForm}>
              <div className={styles.autocompleteContainer}>
                <input
                  type="text"
                  value={guess}
                  onChange={handleGuessChange}
                  placeholder="Enter artist name"
                  autoComplete="off"
                  className={styles.guessInput}
                />
                {suggestions.length > 0 && (
                  <ul className={styles.suggestionsList}>
                    {suggestions.map((suggestion, index) => (
                      <li key={index} onClick={() => handleSuggestionClick(suggestion)}>
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button type="submit" className={styles.guessButton}>
                Guess
              </button>
            </form>
          </div>
        )}

        {phase === 'result' && round && (
          <div className={styles.resultContainer}>
            <div className={isCorrect ? styles.correctBanner : styles.incorrectBanner}>
              {isCorrect ? '🎉 Correct!' : '❌ Incorrect'}
            </div>
            
            <p className={styles.answerReveal}>
              The artist was: <strong>{round.correctAnswer}</strong>
            </p>

            {!isCorrect && (
              <p className={styles.yourGuess}>
                Your guess: <span>{guess}</span>
              </p>
            )}

            <div className={styles.revealedImage}>
              <h3>The Artwork:</h3>
              <img src={round.post.imageUrl} alt="The artwork" />
            </div>

            <div className={styles.resultActions}>
              <button onClick={handlePlayAgain} className={styles.playAgainButton}>
                Play Again
              </button>
              <button onClick={() => navigate('/')} className={styles.homeButton}>
                Back to Home
              </button>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default InsightPage;

